import { getRegionName, getLanguageName } from '@/utils/i18n';
export type LocationMode = 'allow' | 'block';
export type BlockedAction = 'video' | 'url' | 'message';
export type DeviceType = 'desktop' | 'mobile' | 'tablet';
export type LanguageMode = 'allow' | 'block';

export type UtmOperator = 'equals' | 'contains' | 'starts_with' | 'not_contains';
export type UtmAction = 'allow' | 'block';
export type UtmParam = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content';

export interface UtmRule {
  id: string;
  parameter: UtmParam;
  operator: UtmOperator;
  value: string;
  action: UtmAction;
}

export interface VSLProtection {
  id: string;
  name: string;
  videoId: string | null;
  videoName?: string;
  videoThumb?: string;
  enabled: boolean;

  locationMode: LocationMode;
  countries: string[]; // codes
  regions: string[]; // codes

  utmEnabled: boolean;
  utmRules: UtmRule[];

  urlKeyEnabled: boolean;
  urlKeyName: string;
  urlKeyValue: string;

  tokenEnabled: boolean;
  tokenValue: string;

  allowedDevices: DeviceType[];

  languageMode: LanguageMode;
  languages: string[];

  blockProxyVpn: boolean;

  blockedAction: BlockedAction;
  redirectVideoId?: string | null;
  redirectVideoName?: string;
  redirectVideoThumb?: string;
  redirectUrl?: string;
  blockedMessage?: string;

  createdAt: string;
  updatedAt: string;

  // mock metrics
  blockedToday?: number;
  redirects?: number;
}

export const DEFAULT_PROTECTION: Omit<VSLProtection, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  videoId: null,
  enabled: true,
  locationMode: 'allow',
  countries: [],
  regions: [],
  utmEnabled: false,
  utmRules: [],
  urlKeyEnabled: false,
  urlKeyName: '',
  urlKeyValue: '',
  tokenEnabled: false,
  tokenValue: '',
  allowedDevices: ['desktop', 'mobile', 'tablet'],
  languageMode: 'allow',
  languages: [],
  blockProxyVpn: true,
  blockedAction: 'video',
  blockedMessage: 'Este conteúdo não está disponível para o seu acesso.',
};

export const COUNTRIES: { code: string; name: string }[] = [
  { code: 'BR', get name() { return getRegionName('BR', 'Brasil'); } },
  { code: 'US', get name() { return getRegionName('US', 'Estados Unidos'); } },
  { code: 'PT', get name() { return getRegionName('PT', 'Portugal'); } },
  { code: 'AO', get name() { return getRegionName('AO', 'Angola'); } },
  { code: 'MZ', get name() { return getRegionName('MZ', 'Moçambique'); } },
  { code: 'ES', get name() { return getRegionName('ES', 'Espanha'); } },
  { code: 'AR', get name() { return getRegionName('AR', 'Argentina'); } },
  { code: 'MX', get name() { return getRegionName('MX', 'México'); } },
  { code: 'CO', get name() { return getRegionName('CO', 'Colômbia'); } },
  { code: 'CL', get name() { return getRegionName('CL', 'Chile'); } },
  { code: 'PE', get name() { return getRegionName('PE', 'Peru'); } },
  { code: 'UY', get name() { return getRegionName('UY', 'Uruguai'); } },
  { code: 'PY', get name() { return getRegionName('PY', 'Paraguai'); } },
  { code: 'CA', get name() { return getRegionName('CA', 'Canadá'); } },
  { code: 'FR', get name() { return getRegionName('FR', 'França'); } },
  { code: 'IT', get name() { return getRegionName('IT', 'Itália'); } },
  { code: 'DE', get name() { return getRegionName('DE', 'Alemanha'); } },
  { code: 'UK', get name() { return getRegionName('UK', 'Reino Unido'); } },
];

export const REGIONS_BY_COUNTRY: Record<string, { code: string; name: string }[]> = {
  BR: [
    { code: 'AC', name: 'Acre' },
    { code: 'AL', name: 'Alagoas' },
    { code: 'AP', name: 'Amapá' },
    { code: 'AM', name: 'Amazonas' },
    { code: 'BA', name: 'Bahia' },
    { code: 'CE', name: 'Ceará' },
    { code: 'DF', name: 'Distrito Federal' },
    { code: 'ES', name: 'Espírito Santo' },
    { code: 'GO', name: 'Goiás' },
    { code: 'MA', name: 'Maranhão' },
    { code: 'MT', name: 'Mato Grosso' },
    { code: 'MS', name: 'Mato Grosso do Sul' },
    { code: 'MG', name: 'Minas Gerais' },
    { code: 'PA', name: 'Pará' },
    { code: 'PB', name: 'Paraíba' },
    { code: 'PR', name: 'Paraná' },
    { code: 'PE', name: 'Pernambuco' },
    { code: 'PI', name: 'Piauí' },
    { code: 'RJ', name: 'Rio de Janeiro' },
    { code: 'RN', name: 'Rio Grande do Norte' },
    { code: 'RS', name: 'Rio Grande do Sul' },
    { code: 'RO', name: 'Rondônia' },
    { code: 'RR', name: 'Roraima' },
    { code: 'SC', name: 'Santa Catarina' },
    { code: 'SP', name: 'São Paulo' },
    { code: 'SE', name: 'Sergipe' },
    { code: 'TO', name: 'Tocantins' },
  ],
  US: [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'CA', name: 'California' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'IL', name: 'Illinois' },
    { code: 'NY', name: 'New York' },
    { code: 'TX', name: 'Texas' },
    { code: 'WA', name: 'Washington' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NV', name: 'Nevada' },
  ],
  PT: [
    { code: 'LIS', name: 'Lisboa' },
    { code: 'POR', name: 'Porto' },
    { code: 'BRA', name: 'Braga' },
    { code: 'SET', name: 'Setúbal' },
    { code: 'COI', name: 'Coimbra' },
    { code: 'AVE', name: 'Aveiro' },
    { code: 'FAR', name: 'Faro' },
    { code: 'MAD', name: 'Madeira' },
    { code: 'ACO', name: 'Açores' },
  ],
  CO: [
    { code: 'DC', name: 'Bogotá D.C.' },
    { code: 'ANT', name: 'Antioquia' },
    { code: 'ATL', name: 'Atlántico' },
    { code: 'BOL', name: 'Bolívar' },
    { code: 'BOY', name: 'Boyacá' },
    { code: 'CAL', name: 'Caldas' },
    { code: 'CUN', name: 'Cundinamarca' },
    { code: 'SAN', name: 'Santander' },
    { code: 'VAC', name: 'Valle del Cauca' },
  ],
  DE: [
    { code: 'BW', name: 'Baden-Württemberg' },
    { code: 'BY', name: 'Baviera' },
    { code: 'BE', name: 'Berlim' },
    { code: 'BB', name: 'Brandemburgo' },
    { code: 'HB', name: 'Bremen' },
    { code: 'HH', name: 'Hamburgo' },
    { code: 'HE', name: 'Hesse' },
    { code: 'MV', name: 'Mecklemburgo-Pomerânia Ocidental' },
    { code: 'NI', name: 'Baixa Saxônia' },
    { code: 'NW', name: 'Renânia do Norte-Vestfália' },
    { code: 'RP', name: 'Renânia-Palatinado' },
    { code: 'SL', name: 'Sarre' },
    { code: 'SN', name: 'Saxônia' },
    { code: 'ST', name: 'Saxônia-Anhalt' },
    { code: 'SH', name: 'Schleswig-Holstein' },
    { code: 'TH', name: 'Turíngia' },
  ],
  FR: [
    { code: 'IDF', name: 'Île-de-France' },
    { code: 'PAC', name: 'Provence-Alpes-Côte d’Azur' },
    { code: 'ARA', name: 'Auvergne-Rhône-Alpes' },
    { code: 'NAQ', name: 'Nouvelle-Aquitaine' },
    { code: 'OCC', name: 'Occitanie' },
    { code: 'HDF', name: 'Hauts-de-France' },
    { code: 'GES', name: 'Grand Est' },
    { code: 'BRE', name: 'Bretanha' },
    { code: 'NOR', name: 'Normandia' },
  ],
  IT: [
    { code: 'LOM', name: 'Lombardia' },
    { code: 'LAZ', name: 'Lácio' },
    { code: 'CAM', name: 'Campânia' },
    { code: 'SIC', name: 'Sicília' },
    { code: 'VEN', name: 'Vêneto' },
    { code: 'EMR', name: 'Emília-Romanha' },
    { code: 'PIE', name: 'Piemonte' },
    { code: 'TOS', name: 'Toscana' },
    { code: 'CAL', name: 'Calábria' },
  ],
  ES: [
    { code: 'MAD', name: 'Madrid' },
    { code: 'CAT', name: 'Catalunha' },
    { code: 'AND', name: 'Andaluzia' },
    { code: 'VAL', name: 'Valência' },
    { code: 'GAL', name: 'Galícia' },
    { code: 'PVA', name: 'País Basco' },
    { code: 'CLY', name: 'Castela e Leão' },
    { code: 'CAN', name: 'Ilhas Canárias' },
  ],
  UK: [
    { code: 'ENG', name: 'Inglaterra' },
    { code: 'SCT', name: 'Escócia' },
    { code: 'WLS', name: 'País de Gales' },
    { code: 'NIR', name: 'Irlanda do Norte' },
  ],
  CA: [
    { code: 'ON', name: 'Ontário' },
    { code: 'QC', name: 'Quebec' },
    { code: 'BC', name: 'Colúmbia Britânica' },
    { code: 'AB', name: 'Alberta' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'NS', name: 'Nova Escócia' },
  ],
  MX: [
    { code: 'CMX', name: 'Ciudad de México' },
    { code: 'JAL', name: 'Jalisco' },
    { code: 'NLE', name: 'Nuevo León' },
    { code: 'PUE', name: 'Puebla' },
    { code: 'YUC', name: 'Yucatán' },
    { code: 'VER', name: 'Veracruz' },
    { code: 'ROO', name: 'Quintana Roo' },
  ],
  AR: [
    { code: 'BUE', name: 'Buenos Aires' },
    { code: 'CBA', name: 'Córdoba' },
    { code: 'SFE', name: 'Santa Fe' },
    { code: 'MEN', name: 'Mendoza' },
    { code: 'TUC', name: 'Tucumán' },
    { code: 'SAL', name: 'Salta' },
  ],
  CL: [
    { code: 'RM', name: 'Región Metropolitana' },
    { code: 'VAL', name: 'Valparaíso' },
    { code: 'BIO', name: 'Biobío' },
    { code: 'ANT', name: 'Antofagasta' },
    { code: 'LAG', name: 'Los Lagos' },
  ],
  PE: [
    { code: 'LIM', name: 'Lima' },
    { code: 'ARE', name: 'Arequipa' },
    { code: 'CUS', name: 'Cusco' },
    { code: 'LIB', name: 'La Libertad' },
    { code: 'PIU', name: 'Piura' },
  ],
  UY: [
    { code: 'MON', name: 'Montevidéu' },
    { code: 'CAN', name: 'Canelones' },
    { code: 'MAL', name: 'Maldonado' },
  ],
  PY: [
    { code: 'ASU', name: 'Assunção' },
    { code: 'CEN', name: 'Central' },
    { code: 'ALT', name: 'Alto Paraná' },
  ],
  AO: [
    { code: 'LUA', name: 'Luanda' },
    { code: 'BEN', name: 'Benguela' },
    { code: 'HUA', name: 'Huambo' },
  ],
  MZ: [
    { code: 'MAP', name: 'Maputo' },
    { code: 'SOF', name: 'Sofala' },
    { code: 'NAM', name: 'Nampula' },
  ],
};

export const LANGUAGES: { code: string; name: string }[] = [
  { code: 'pt', get name() { return getLanguageName('pt', 'Português'); } },
  { code: 'en', get name() { return getLanguageName('en', 'Inglês'); } },
  { code: 'es', get name() { return getLanguageName('es', 'Espanhol'); } },
  { code: 'fr', get name() { return getLanguageName('fr', 'Francês'); } },
  { code: 'de', get name() { return getLanguageName('de', 'Alemão'); } },
  { code: 'it', get name() { return getLanguageName('it', 'Italiano'); } },
  { code: 'zh', get name() { return getLanguageName('zh', 'Chinês mandarim'); } },
  { code: 'hi', get name() { return getLanguageName('hi', 'Hindi'); } },
  { code: 'ar', get name() { return getLanguageName('ar', 'Árabe'); } },
];
